import {
  useEffect, useMemo, useCallback, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, message } from 'antd';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import validator from 'validator';

import { useTranslation } from 'react-i18next';

import { findRouteWithKey } from '@routes/routes';
import invertersApi from '@api/invertersApi';
import alarmsActions from '@state/inverters/actions';
import variablesActions from '@state/variables/actions';

function useInvertersEditCreate() {

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();

  const { alarm_id } = useParams();
  const [form] = Form.useForm();

  const createEditValues = useSelector(({ inverters: a }) => a.createEditValues);
  const isEditDefault = useSelector(({ inverters: a }) => a.isEditDefault);

  const [container, setContainer] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isForCreate] = useState(!alarm_id);
  const [loadingAlarmToEdit, setLoadingAlarmToEdit] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const pathBefore = useMemo(() => {

    const { path: fromDefaults } = findRouteWithKey('RULES__DEFAULT');
    const { path: fromDynamic } = findRouteWithKey('RULES__INVERTERS');

    const paths = [fromDefaults, fromDynamic];
    return paths.find((path) => match.path.includes(path));

  }, [match]);

  const breadcrumbData = useMemo(() => {

    const currentSection = isForCreate
      ? t('views.dynamic_alarms_create_edit.breadcrumb.create_rule_inverters')
      : `${t('views.dynamic_alarms_create_edit.breadcrumb.edit_inverters')} / N° ${alarm_id}`;

    const { path: fromDefaults } = findRouteWithKey('RULES__DEFAULT');

    if (match.path.includes(fromDefaults)) {

      return [
        [fromDefaults, t('views.dynamic_alarms_create_edit.breadcrumb.inverters_rules')],
        [null, currentSection],
      ];

    }

    const { path: fromDynamic } = findRouteWithKey('RULES__INVERTERS');
    return [
      [fromDynamic, t('views.dynamic_alarms_create_edit.breadcrumb.inverters_rules')],
      [null, currentSection],
    ];

  }, [isForCreate, alarm_id, match, t]);

  const initialForm = useMemo(() => ({
    name: '',
    // origin: '',
    // description: null,
    // severity: 'Critical',
    is_active: true,
    conditions: [],
    actions: [],
    newValue: 'fail',
  }), []);

  const formOnValuesChange = useCallback(
    (changedValues) => {

      const newEditValues = { ...createEditValues };

      Object.entries(changedValues).forEach(([key, value]) => {

        newEditValues[key] = value;

      });

      dispatch(alarmsActions.setCreateEditValues(newEditValues));

    },
    // eslint-disable-next-line
    [createEditValues]
  );

  const isFirstStepValid = useCallback(async () => {

    try {

      await form.validateFields();
      return true;

    } catch {

      return false;

    }

  }, [form]);

  const isSecondStepValid = useCallback(() => {

    // Verificar que al menos haya un grupo
    const { conditions } = createEditValues || {};
    if (!conditions || conditions.length === 0) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.add_at_least_one_condition'));
      return false;

    }

    // Verificar que los grupos tengan al menos una condición
    const filtersEmptyGroup = conditions.filter((orConditions) => orConditions.length === 0);
    if (filtersEmptyGroup.length > 0) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.group_without_condition'));
      return false;

    }

    // Verificar que las orConditions este completas
    const allOrConditions = conditions.flat();

    let isValidAllOrConditions = true;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < allOrConditions.length; i++) {

      const orCondition = allOrConditions[i];
      const { params, field, type } = orCondition;

      if (!type) {

        isValidAllOrConditions = false;
        break;

      }
      if (!field) {

        isValidAllOrConditions = false;
        break;

      }
      if (!params) {

        isValidAllOrConditions = false;
        break;

      }

    }

    if (!isValidAllOrConditions) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.completed_conditions'));
      return false;

    }

    return true;

  }, [createEditValues, t]);

  const isStepValidate = useCallback(async () => {

    switch (currentStep) {

      case 0:
        // eslint-disable-next-line no-return-await
        return await isFirstStepValid();
      case 1:
        return isSecondStepValid();
      default:
        return false;

    }
    // eslint-disable-next-line
  }, [currentStep, createEditValues]);

  const onPrevious = useCallback(() => {

    setCurrentStep(currentStep - 1);

  }, [currentStep]);

  const onNext = useCallback(async () => {

    const isStepValid = await isStepValidate();
    if (isStepValid) setCurrentStep(currentStep + 1);
    // eslint-disable-next-line
  }, [currentStep, isStepValidate]);

  const onSave = useCallback(() => {

    const { actions } = createEditValues || {};

    const allUrls = [];
    const allEmails = [];

    actions.forEach((action) => {

      const { type, params } = action;
      switch (type) {

        case 'webhook':
          allUrls.push(...allUrls, ...params.urls);
          break;

        case 'email':
          allEmails.push(...allEmails, ...params.recipients);
          break;

        default:
          break;

      }

    });

    // Check actions
    if (actions.length < 1) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.add_action'));
      return;

    }

    // Check urls
    if (allUrls.some((url) => url.length === 0)) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.delete_empty_fields_url'));
      return;

    }

    // Check emails
    if (allEmails.some((email) => email.length === 0)) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.delete_empty_fields_email'));
      return;

    }
    // Check emails validate
    if (!allEmails.every((email) => validator.isEmail(email))) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.error_fields_email'));
      return;

    }

    const {
      id, created_at, is_active, updated_at,
    } = createEditValues;
    const body = createEditValues;
    if (id) delete body.id;
    if (created_at) delete body.created_at;
    if (is_active) delete body.is_active;
    if (updated_at) delete body.updated_at;

    setSaveLoading(true);

    const new_body = { ...body };
    const array = [];
    new_body.conditions.forEach((condition) => { // sacando condiciones

      const rules = [];
      condition.forEach((data) => {

        const value = {
          type: data.type,
          field: data.field,
          values: data.values || data.params.values,
          // ? data.values : [data.params.values[0].toString()],
          // operator: data.params.operator,
        };
        rules.push(value);

      });
      array.push(rules);

    });
    const action_array = [];
    new_body.actions.forEach((data) => { // sacando acciones

      const action = {
        type: data.type,
        recipients: (data.params.recipients) ? data.params.recipients : [data.params.urls],
      };
      action_array.push(action);

    });
    new_body.conditions = array;
    new_body.actions = action_array;
    invertersApi
      .createUpdateAlarm({ id, body: new_body })
      .then((response) => {

        // eslint-disable-next-line no-shadow
        const array = [];
        response.conditions.forEach((condition) => {

          const rules = [];
          condition.forEach((data) => {

            const value = {
              type: data.type,
              field: data.field,
              params: {
                value: data.value,
                // operator: data.operator,
              },
            };
            rules.push(value);

          });
          array.push(rules);

        });
        // eslint-disable-next-line no-shadow
        const action_array = [];
        response.actions.forEach((data) => {

          const action = {
            type: data.type,
            params: {
              recipients: [data.recipients],
            },
          };
          action_array.push(action);

        });
        response.conditions = array;
        response.actions = action_array;

        setSaveLoading(false);

        if (isEditDefault) {

          dispatch(variablesActions.setDefaultAlarms(null));
          history.replace(pathBefore || '/reglas/inversores');
          message.success(t('views.dynamic_alarms_create_edit.feedbacks.created_alarm_inverters'));

        }

        if (!id) {

          dispatch(alarmsActions.addAlarmToListOrSumCount(response));
          history.replace(pathBefore || '/reglas/inversores');
          message.success(t('views.dynamic_alarms_create_edit.feedbacks.created_alarm_inverters'));
          return;

        }

        dispatch(alarmsActions.editAlarm(response));
        history.push(pathBefore || '/reglas/inversores');
        message.success(t('views.dynamic_alarms_create_edit.feedbacks.updated_alarm_inverters'));

      })
      .catch((resError) => {

        // eslint-disable-next-line
        console.log('error', resError);
        setSaveLoading(false);
        const {
          // eslint-disable-next-line no-shadow
          data: { actions, detail },
          // fem,
        } = resError;
        if (actions && typeof actions === 'object') {

          if ('urls' in actions) {

            message.error(t('views.dynamic_alarms_create_edit.feedbacks.verify_urls'));
            return;

          }

        }
        message.error(t(detail[0].msg));

      });
    // eslint-disable-next-line
  }, [createEditValues, pathBefore, isEditDefault]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {

    dispatch(alarmsActions.setIsEditDefault({ forceIsEdit: false }));

    // use efect for resquest alarm to edit
    if (alarm_id) {

      const abortController = new AbortController();
      const { signal } = abortController;

      setLoadingAlarmToEdit(true);
      invertersApi
        .getAlarmById({ id: alarm_id, signal })
        .then((response) => {

          const array = [];
          response.conditions.forEach((condition) => { // conditions

            const rules = [];
            condition.forEach((data) => {

              const value = {
                type: data.type,
                field: data.field,
                params: {
                  values: data.values,
                  // operator: data.operator,
                },
              };
              rules.push(value);

            });
            array.push(rules);

          });
          const action_array = [];
          response.actions.forEach((data) => { // actions

            const action = {
              type: data.type,
              params: {
                recipients: data.recipients,
              },
            };
            action_array.push(action);

          });
          response.conditions = array;
          response.actions = action_array;
          dispatch(alarmsActions.setIsEditDefault(response));
          dispatch(alarmsActions.setCreateEditValues(response));
          setLoadingAlarmToEdit(false);

        })
        .catch((resError) => {

          // eslint-disable-next-line no-console
          console.log('inverter_err', resError);
          if (signal.aborted) return;
          const { fem } = resError;
          // eslint-disable-next-line no-console
          console.log(t(fem));
          setLoadingAlarmToEdit(false);

        });

      // eslint-disable-next-line
      return () => {

        abortController.abort();

      };

    }

    dispatch(alarmsActions.setCreateEditValues(initialForm));

  // eslint-disable-next-line
  }, [alarm_id, dispatch, initialForm]);

  useEffect(() => {

    if (!isEditDefault) {

      setCurrentStep(0);
      return;

    }
    setCurrentStep(2);

  }, [isEditDefault]);

  // Limpia el CreateEditValues del redux al cerrar sección
  // eslint-disable-next-line
  useEffect(() => () => dispatch(alarmsActions.setCreateEditValues(null)), []);

  return {
    breadcrumbData,
    container,
    setContainer,
    form,
    formOnValuesChange,
    createEditValues,
    loadingAlarmToEdit,
    currentStep,
    onPrevious,
    onNext,
    onSave,
    saveLoading,
    isEditDefault,
  };

}

export default useInvertersEditCreate;
